/**
 * Mail UI Utility — Paylaşılan mail görünüm yardımcıları.
 * posta, mailler, mail-detay bileşenlerinde tekrar eden kodları tek noktada toplar.
 */

/** Avatar renk paleti (deterministic hash ile kullanılır) */
export const MAIL_AVATAR_RENKLER = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2', '#f5222d', '#faad14'];

/** "Ad Soyad <email>" → "Ad Soyad" (MIME decode destekli) */
export function gonderenIsim(kimden: string): string {
  const decoded = mimeDecodeIsim(kimden || '');
  const m = decoded.match(/^(.+?)\s*<.+>$/);
  return m ? m[1].trim() : decoded.split('@')[0] || '';
}

/** "Ad Soyad <email>" → "email" */
export function gonderenAdres(kimden: string): string {
  const m = kimden?.match(/<(.+)>/);
  return m ? m[1] : kimden || '';
}

/** Gönderen isminin ilk harfi (avatar için) */
export function avatarHarf(kimden: string): string {
  return gonderenIsim(kimden)?.charAt(0)?.toUpperCase() || '?';
}

/** İsimden deterministik renk (hash) */
export function avatarRenk(kimden: string): string {
  const isim = gonderenIsim(kimden);
  let hash = 0;
  for (let i = 0; i < isim.length; i++) hash = isim.charCodeAt(i) + ((hash << 5) - hash);
  return MAIL_AVATAR_RENKLER[Math.abs(hash) % MAIL_AVATAR_RENKLER.length];
}

/** Mail metin önizlemesi (HTML strip) */
export function mailSnippet(icerik: string, icerikHtml?: string, maxLen = 80): string {
  const raw = icerik || icerikHtml?.replace(/<[^>]*>/g, '') || '';
  return raw.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().substring(0, maxLen);
}

/** MIME RFC 2047 encoded metni decode et (charset destekli) */
export function mimeDecodeIsim(isim: string): string {
  if (!isim) return '';
  return isim.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (_, charset: string, encoding: string, text: string) => {
    let bytes: number[];
    if (encoding.toUpperCase() === 'B') {
      try {
        const raw = atob(text);
        bytes = Array.from(raw, c => c.charCodeAt(0));
      } catch { return text; }
    } else {
      bytes = [];
      const qText = text.replace(/_/g, ' ');
      for (let i = 0; i < qText.length; i++) {
        if (qText[i] === '=' && i + 2 < qText.length) {
          bytes.push(parseInt(qText.substring(i + 1, i + 3), 16));
          i += 2;
        } else {
          bytes.push(qText.charCodeAt(i));
        }
      }
    }
    try {
      const cs = charset.toLowerCase().replace(/^windows-/, 'windows-');
      if (cs === 'utf-8' || cs === 'utf8') {
        return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
      }
      const label = cs === 'iso-8859-9' ? 'windows-1254' : cs;
      return new TextDecoder(label).decode(new Uint8Array(bytes));
    } catch {
      return String.fromCharCode(...bytes);
    }
  });
}

/** Byte → okunabilir boyut */
export function dosyaBoyutFormatla(boyut: number): string {
  if (!boyut || boyut < 0) return '0 B';
  if (boyut < 1024) return boyut + ' B';
  if (boyut < 1024 * 1024) return (boyut / 1024).toFixed(1) + ' KB';
  return (boyut / (1024 * 1024)).toFixed(1) + ' MB';
}

/** FAPI dosya indirme URL'i oluştur */
export function ekIndirUrl(dosyaId: string): string {
  if (!dosyaId) return '';
  const fapiUrl = (window as any).FikoYT?.fapiUrl || `${location.protocol}//${location.hostname}:9000`;
  return `${fapiUrl}/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${dosyaId}.dosya`;
}

// ─── Dosya Tipi Yardımcıları ──────────────────────────────────────

/** Dosya uzantısını al */
export function dosyaUzantisi(adi?: string): string {
  if (!adi) return '';
  const parts = adi.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

/** Dosya tipine göre CSS renk sınıfı */
export function ekRenkSinifi(tur: string, adi?: string): string {
  const ext = dosyaUzantisi(adi);
  if (['stp', 'step', 'igs', 'iges'].includes(ext)) return 'cad';
  if (['wrl', 'vrml'].includes(ext)) return 'cad3d';
  if (['fikoperasyon', 'fop'].includes(ext)) return 'fiko-op';
  if (['fikoopgl', 'fogl'].includes(ext)) return 'fiko-gl';
  if (['fikocizim', 'fcz'].includes(ext)) return 'fiko-cz';
  if (tur?.includes('zip') || tur?.includes('rar') || ext === '7z') return 'zip';
  if (tur?.includes('pdf') || ext === 'pdf') return 'pdf';
  if (tur?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
  if (tur?.includes('word') || tur?.includes('document') || ['doc', 'docx'].includes(ext)) return 'word';
  if (tur?.includes('excel') || tur?.includes('sheet') || ['xls', 'xlsx'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['stl', 'obj', '3ds', 'fbx', 'glb', 'gltf'].includes(ext)) return 'cad3d';
  return '';
}

/** Dosya tipine göre Ng-Zorro ikon */
export function dosyaIkon(tur: string, adi?: string): string {
  const ext = dosyaUzantisi(adi);
  if (['stp', 'step', 'igs', 'iges'].includes(ext)) return 'build';
  if (['wrl', 'vrml', 'stl', 'obj', '3ds', 'fbx', 'glb', 'gltf'].includes(ext)) return 'codepen';
  if (['fikoperasyon', 'fop'].includes(ext)) return 'tool';
  if (['fikoopgl', 'fogl'].includes(ext)) return 'eye';
  if (['fikocizim', 'fcz'].includes(ext)) return 'highlight';
  if (tur?.includes('pdf') || ext === 'pdf') return 'file-pdf';
  if (tur?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'file-image';
  if (tur?.includes('word') || ['doc', 'docx'].includes(ext)) return 'file-word';
  if (tur?.includes('excel') || ['xls', 'xlsx'].includes(ext)) return 'file-excel';
  if (['ppt', 'pptx'].includes(ext)) return 'file-ppt';
  if (tur?.includes('zip') || tur?.includes('rar') || ['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) return 'file-zip';
  if (tur?.includes('text') || ['txt', 'csv', 'log'].includes(ext)) return 'file-text';
  return 'file';
}

/** Türkçe karakter uyumlu regex (İ↔i, I↔ı, Ş↔ş, Ç↔ç, Ö↔ö, Ü↔ü, Ğ↔ğ) — regex-safe */
export function turkceRegex(metin: string): string {
  const ciftler: [string, string][] = [
    ['i', 'İ'], ['ı', 'I'], ['ş', 'Ş'], ['ç', 'Ç'], ['ö', 'Ö'], ['ü', 'Ü'], ['ğ', 'Ğ'],
  ];
  let sonuc = '';
  for (const ch of metin) {
    const cift = ciftler.find(([a, b]) => ch === a || ch === b || ch === a.toUpperCase() || ch === b.toLowerCase());
    if (cift) {
      sonuc += `[${cift[0]}${cift[1]}]`;
    } else {
      // Regex metacharacter escape (ReDoS koruması)
      sonuc += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }
  return sonuc;
}
